import { Box, Button, Skeleton, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { colors } from '@src/styles';
import { OrgCanonicalScriptsListStepProps } from './types';
import { monoFontFamily, noticeSX, outlineGoldButtonSX, skeletonSX } from '../../scriptsStyles';

const RequirementTag = ({ label }: { label: string }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      height: 20,
      px: 0.875,
      borderRadius: '999px',
      border: `1px solid ${colors.brand.line}`,
      color: colors.brand.textSecondary,
      fontFamily: monoFontFamily,
      fontSize: '9.5px',
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </Box>
);

export const OrgCanonicalScriptsListStep = ({
  templates,
  selectedTemplate,
  onTemplateChange,
  onDownloadTemplate,
  isLoadingTemplates,
}: OrgCanonicalScriptsListStepProps) => {
  if (isLoadingTemplates) {
    return (
      <Box aria-busy="true" sx={{ display: 'grid', gap: 1 }}>
        {[0, 1, 2].map((item) => (
          <Skeleton key={item} variant="rounded" height={56} sx={{ ...skeletonSX, borderRadius: '10px' }} />
        ))}
      </Box>
    );
  }

  if (templates.length === 0) {
    return (
      <Box sx={noticeSX(colors.status.running)}>
        <InfoOutlinedIcon />
        Nenhum script canônico disponível para essa organização.
      </Box>
    );
  }

  const currentTemplate = templates.find((template) => template.id === selectedTemplate);

  return (
    <Box>
      <Typography sx={{ color: colors.brand.textSecondary, fontSize: '13.5px', mb: 1.5 }}>
        Escolha o script canônico que será criado.
      </Typography>

      <Box
        role="radiogroup"
        aria-label="Scripts canônicos"
        sx={{ display: 'grid', gap: 1, maxHeight: 300, overflowY: 'auto', pr: 0.5 }}
      >
        {templates.map((template) => {
          const isSelected = template.id === selectedTemplate;
          return (
            <Box
              key={template.id}
              component="button"
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => onTemplateChange(template.id)}
              sx={{
                all: 'unset',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                padding: '12px 14px',
                borderRadius: '10px',
                cursor: 'pointer',
                border: `1px solid ${isSelected ? colors.primary.yellow : colors.brand.line}`,
                backgroundColor: isSelected ? 'rgba(255, 160, 21, 0.08)' : colors.background.primary,
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
                '&:hover': { borderColor: isSelected ? colors.primary.yellow : 'rgba(255, 160, 21, 0.45)' },
                '&:focus-visible': { outline: `2px solid ${colors.primary.yellow}`, outlineOffset: 2 },
              }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 16,
                  height: 16,
                  flexShrink: 0,
                  borderRadius: '50%',
                  border: `1.5px solid ${isSelected ? colors.primary.yellow : colors.brand.textMuted}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isSelected && (
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.primary.yellow }} />
                )}
              </Box>
              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontFamily: monoFontFamily,
                  fontSize: '13px',
                  color: isSelected ? colors.primary.white : colors.brand.textStrong,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {template.key}
              </Typography>
              {template.modelDocumentPath && <RequirementTag label="Planilha" />}
              {Boolean(template.schema) && <RequirementTag label="Configuração" />}
            </Box>
          );
        })}
      </Box>

      {selectedTemplate && currentTemplate?.modelDocumentPath && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            mt: 2,
            padding: '12px 14px',
            borderRadius: '10px',
            border: `1px dashed ${colors.brand.line}`,
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 20, color: `${colors.brand.textSecondary} !important` }} />
          <Typography sx={{ flex: 1, minWidth: 200, fontSize: '13px', color: colors.brand.textSecondary }}>
            Este script usa uma planilha. Baixe o modelo, preencha e envie na próxima etapa.
          </Typography>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={onDownloadTemplate} sx={outlineGoldButtonSX}>
            Baixar planilha padrão
          </Button>
        </Box>
      )}
    </Box>
  );
};
